import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  ClipboardCheck,
  Dumbbell,
  HeartHandshake,
  Leaf,
  MessageCircleQuestion,
  Moon,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  Users,
} from "lucide-react";
import { cachedGet } from "@/lib/publicApi";
import { EmergencyBar, Skeleton } from "./SaudeLayout";

const HELP_CARDS = [
  {
    slug: "dependencia-de-alcool",
    title: "Dependência de álcool, substâncias e medicamentos",
    body:
      "Cuidado integrado desde a avaliação e, quando indicada, a desintoxicação, até a prevenção de recaídas e a reinserção social.",
  },
  {
    slug: "dependencia-em-jogos-e-apostas",
    title: "Problemas com jogos",
    body: "Acompanhamento para recuperar autonomia e equilíbrio financeiro.",
  },
  {
    slug: "depressao",
    title: "Depressão",
    body:
      "Avaliação individual e acompanhamento psiquiátrico e psicológico voltados à recuperação e à qualidade de vida.",
  },
  {
    slug: "burnout",
    title: "Ansiedade e burnout",
    body: "Cuidado para reduzir o sofrimento, reorganizar a rotina e construir uma retomada saudável.",
  },
  {
    slug: "tratamentos",
    title: "Outros transtornos de saúde mental",
    body: "Avaliação especializada para transtornos associados, respeitando as necessidades de cada pessoa.",
  },
];

const DIFFERENTIALS = [
  { icon: HeartHandshake, label: "Tratamento individualizado e humanizado" },
  { icon: Users, label: "Equipe multidisciplinar especializada" },
  { icon: ShieldCheck, label: "Enfermagem e acompanhamento 24 horas" },
  { icon: MessageCircleQuestion, label: "Orientação para a rede de apoio" },
  { icon: Dumbbell, label: "Nutrição, atividade física e hotelaria" },
  { icon: Leaf, label: "Ambiente integrado à natureza" },
];

const STEPS = [
  ["Primeiro contato", "Conversa sigilosa para entender a necessidade."],
  ["Avaliação", "Análise clínica e definição da modalidade adequada."],
  ["Plano individualizado", "Organização do cuidado conforme cada caso."],
  [
    "Acompanhamento",
    "Evolução clínica, prevenção de recaídas e preparação para a continuidade do cuidado.",
  ],
];

const TEAM = [
  "Psiquiatria",
  "Psicologia",
  "Enfermagem 24 horas",
  "Terapeutas e conselheiros",
  "Terapia familiar",
  "Neuropsicologia",
  "Nutrição",
  "Educação física",
  "Acompanhamento clínico",
];

export default function SaudeHome() {
  const [data, setData] = useState(null);
  const [fromCache, setFromCache] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const result = await cachedGet("/home");
        setData(result.data);
        setFromCache(result.fromCache);
      } catch {
        setData({ settings: {}, treatments: [], facility_preview: [] });
      }
    })();
  }, []);

  if (!data) return <Skeleton lines={5} testId="home-skeleton" />;

  const home = data.settings?.home || {};
  const contact = data.settings?.contact || {};
  const treatmentSlugs = new Set((data.treatments || []).map((t) => t.slug));
  const preview = data.facility_preview || [];

  return (
    <div data-testid="saude-home-page" className="space-y-12">
      {/* HERO */}
      <section data-testid="home-hero" className="er-rise">
        <img
          data-testid="home-hero-photo"
          src={`${process.env.REACT_APP_BACKEND_URL}/api/v1/media/estrutura/ADV6816-screen.jpg`}
          alt="Cachoeira em meio à área arborizada da Clínica Erimus, em Atibaia"
          className="h-[clamp(170px,23vh,210px)] w-full rounded-2xl object-cover object-[68%_45%]"
        />
        <p className="er-label mt-4 text-[13px] tracking-[0.14em]">{home.hero_kicker || "Sua vida. Sua recuperação. Nossa missão."}</p>
        <h1 className="mt-3 font-display text-[clamp(1.75rem,7.5vw,2.25rem)] font-semibold leading-[1.2] tracking-tight text-ink-900 sm:text-4xl">
          {home.hero_title || "Cuidado em saúde mental com acolhimento, ciência e propósito."}
        </h1>
        <p className="mt-3.5 text-[15.5px] leading-[1.55] text-ink-600 sm:text-base">{home.hero_body}</p>
        <p data-testid="home-hero-info" className="mt-3.5 text-[15px] font-medium text-sage-700">
          {home.hero_info || "Atendimento 24 horas • Atibaia, SP"}
        </p>
        {fromCache ? <p className="mt-2 text-xs text-ink-400">Exibindo conteúdo salvo no dispositivo.</p> : null}

        <div className="mt-5 space-y-3">
          <Link
            to="/saude/fale-com-a-erimus/avaliacao"
            data-testid="home-cta-evaluation"
            className="flex min-h-[56px] items-center justify-between rounded-full bg-sage-600 px-6 text-base font-semibold text-white transition-colors hover:bg-sage-700"
          >
            Solicitar avaliação <ClipboardCheck size={19} />
          </Link>
          <Link
            to="/saude/fale-com-a-erimus/pergunta"
            data-testid="home-cta-question"
            className="flex min-h-[56px] items-center justify-between rounded-full border border-sage-600/40 bg-white px-6 text-base font-semibold text-sage-700 transition-colors hover:bg-sage-50"
          >
            Tirar dúvida anonimamente <MessageCircleQuestion size={19} />
          </Link>
        </div>
      </section>

      {/* COMO PODEMOS AJUDAR */}
      <section data-testid="home-how-we-help" className="!mt-7">
        <h2 className="font-display text-lg font-semibold text-ink-900 md:text-lg">Como podemos ajudar</h2>
        <div className="mt-5 space-y-3">
          {HELP_CARDS.map((card) => {
            const to = treatmentSlugs.has(card.slug) ? `/saude/tratamentos/${card.slug}` : "/saude/tratamentos";
            return (
              <Link
                key={card.slug}
                to={to}
                data-testid={`home-help-${card.slug}`}
                className="er-card flex items-start gap-3"
              >
                <Sparkles size={18} className="mt-1 shrink-0 text-sage-600" aria-hidden="true" />
                <span>
                  <span className="block font-display text-base font-medium text-ink-900">{card.title}</span>
                  <span className="mt-1 block text-sm leading-relaxed text-ink-500">{card.body}</span>
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* POR QUE ESCOLHER A ERIMUS */}
      <section data-testid="home-why-erimus">
        <h2 className="font-display text-lg font-semibold text-ink-900 md:text-lg">Por que escolher a Erimus</h2>
        <ul className="mt-5 space-y-3">
          {DIFFERENTIALS.map((d) => (
            <li key={d.label} className="flex items-start gap-3 rounded-2xl bg-white px-4 py-3.5 ring-1 ring-border">
              <d.icon size={18} className="mt-0.5 shrink-0 text-sage-600" aria-hidden="true" />
              <span className="text-sm leading-relaxed text-ink-700">{d.label}</span>
            </li>
          ))}
        </ul>
        <p data-testid="home-belief-note" className="mt-5 rounded-2xl bg-sand px-5 py-4 text-sm leading-relaxed text-ink-600">
          {home.belief_note}
        </p>
      </section>

      {/* COMO FUNCIONA */}
      <section data-testid="home-how-it-works">
        <h2 className="font-display text-lg font-semibold text-ink-900 md:text-lg">Como funciona</h2>
        <ol className="mt-5 space-y-4">
          {STEPS.map(([title, body], i) => (
            <li key={title} className="flex gap-4">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-sage-600 text-xs font-bold text-white">
                {i + 1}
              </span>
              <span>
                <span className="block font-display text-base font-medium text-ink-900">{title}</span>
                <span className="mt-1 block text-sm leading-relaxed text-ink-500">{body}</span>
              </span>
            </li>
          ))}
        </ol>
        <p className="mt-5 text-xs leading-relaxed text-ink-400">
          As etapas, modalidades e a duração do cuidado dependem sempre da avaliação clínica de cada pessoa.
        </p>
      </section>

      {/* EQUIPE INTEGRADA */}
      <section data-testid="home-team">
        <h2 className="font-display text-lg font-semibold text-ink-900 md:text-lg">Equipe integrada</h2>
        <p className="mt-4 text-sm leading-relaxed text-ink-600">{home.team_note}</p>
        <div className="mt-5 flex flex-wrap gap-2">
          {TEAM.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs text-ink-600 ring-1 ring-border"
            >
              <Stethoscope size={13} className="text-sage-600" aria-hidden="true" /> {t}
            </span>
          ))}
        </div>
      </section>

      {/* ESTRUTURA */}
      <section data-testid="home-facility">
        <h2 className="font-display text-lg font-semibold text-ink-900 md:text-lg">Conheça nossa estrutura</h2>
        <p className="mt-3 text-sm leading-relaxed text-ink-600">{home.facility_teaser}</p>
        {preview.length ? (
          <div className="mt-5 grid grid-cols-3 gap-2">
            {preview.map((p) => (
              <img
                key={p.id}
                data-testid={`home-facility-photo-${p.id}`}
                src={`${process.env.REACT_APP_BACKEND_URL}${p.thumb_url}`}
                alt={p.alt}
                loading="lazy"
                className="h-24 w-full rounded-xl object-cover"
              />
            ))}
          </div>
        ) : (
          <p data-testid="home-facility-empty" className="mt-4 rounded-2xl bg-sand px-5 py-4 text-sm text-ink-500">
            As fotos da estrutura serão publicadas em breve.
          </p>
        )}
        <Link
          to="/saude/estrutura"
          data-testid="home-cta-facility"
          className="mt-5 flex min-h-[52px] items-center justify-between rounded-full border border-sage-600/40 bg-white px-6 text-sm font-semibold text-sage-700 transition-colors hover:bg-sage-50"
        >
          Conhecer a estrutura <Building2 size={17} />
        </Link>
      </section>

      {/* CTA FINAL */}
      <section data-testid="home-final-cta" className="rounded-3xl er-brand-band px-6 py-8 text-white">
        <Moon size={22} className="text-white/70" aria-hidden="true" />
        <h2 className="mt-4 font-display text-2xl font-semibold leading-tight">
          {home.final_cta_title || "Você não precisa ter todas as respostas para dar o primeiro passo."}
        </h2>
        <div className="mt-7 space-y-3">
          <Link
            to="/saude/fale-com-a-erimus/avaliacao"
            data-testid="home-final-cta-evaluation"
            className="flex min-h-[52px] items-center justify-between rounded-full bg-white px-6 text-sm font-semibold text-sage-700"
          >
            Solicitar avaliação <ArrowRight size={17} />
          </Link>
          <Link
            to="/saude/fale-com-a-erimus/pergunta"
            data-testid="home-final-cta-question"
            className="flex min-h-[52px] items-center justify-between rounded-full border border-white/40 px-6 text-sm font-semibold text-white"
          >
            Enviar dúvida anônima <ArrowRight size={17} />
          </Link>
        </div>
        {contact.insurance_note ? (
          <div data-testid="home-insurance" className="mt-7 border-t border-white/20 pt-5">
            <p className="text-sm font-semibold text-white">
              {home.insurance_cta || "Consulte convênios e possibilidades de atendimento"}
            </p>
            <p className="mt-2 text-sm leading-relaxed text-white/80">{contact.insurance_note}</p>
          </div>
        ) : null}
      </section>

      <p data-testid="home-emergency-note" className="text-xs leading-relaxed text-ink-400">
        {home.emergency_note || "Este aplicativo não substitui atendimento de emergência."}
      </p>
      <EmergencyBar />
    </div>
  );
}
